import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Button from "../Common/Button/Button";
import DataTable from "../Common/DataTable/DataTable";
import IncrementalPagination from "../Common/DataTable/IncrementalPagination";
import ScreenLoader from "../Common/ScreenLoader/ScreenLoader";
import AppSnackbar from "../Common/Snackbar/AppSnackbar";
import AppRadioGroup from "../Common/FormInputs/AppRadioGroup";
import { useDesignTokens } from "../../theme/useDesignTokens";
import customConfig from "../../config";
import {
  fetchCachedUserSpecificNotifications,
  fetchInAppNotificationPage,
  getInAppNotificationErrorMessage,
  getInAppNotifications,
  invalidateInAppNotificationListCache,
  parseInAppNotificationList,
} from "../../action/inAppNotification";
import {
  isValidInAppNotificationListItem,
  matchesTargetType,
  parseExpiryValue,
  sumTargetUserCounts,
} from "../../utils/inAppNotificationUtils";
import {
  buildNotificationListRouteState,
  buildNotificationListState,
  DEFAULT_NOTIFICATION_LIST_TAB_UI,
  getInAppNotificationPermission,
  IN_APP_NOTIFICATION_ROUTES,
  inAppListTableShellSx,
  inAppPrimaryActionSx,
  inAppRadioGroupResponsiveSx,
  inAppToolbarRowSx,
  resolveNotificationListStateFromLocation,
  setPendingListPagination,
  TARGET_TYPE_OPTIONS,
  TOTAL_TARGETED_USERS_LABEL,
} from "../../constants/inAppNotificationConstant";
import { createInAppNotificationColumns } from "./inAppNotificationColumns";
import DeleteNotificationDialog from "./DeleteNotificationDialog";
import NotificationTargetUserSummary from "./NotificationTargetUserSummary";

const sortNotifications = (items, orderBy, order) => {
  if (!orderBy) return items;

  return [...items].sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    if (orderBy === "expiry") {
      aValue = parseExpiryValue(a.expiry)?.getTime() ?? 0;
      bValue = parseExpiryValue(b.expiry)?.getTime() ?? 0;
    }

    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return order === "asc" ? 1 : -1;
    if (bValue == null) return order === "asc" ? -1 : 1;

    if (aValue < bValue) return order === "asc" ? -1 : 1;
    if (aValue > bValue) return order === "asc" ? 1 : -1;
    return 0;
  });
};

const NotificationsTab = ({ userRoles = [], userLevel = 0 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isCompactTable = useMediaQuery(theme.breakpoints.down("sm"));
  const { colors } = useDesignTokens();

  const initialTabStateRef = useRef(null);
  if (initialTabStateRef.current === null) {
    if (location.state?.refresh) {
      invalidateInAppNotificationListCache();
    }
    initialTabStateRef.current = {
      ...DEFAULT_NOTIFICATION_LIST_TAB_UI,
      ...(resolveNotificationListStateFromLocation(location) ?? {}),
    };
  }
  const initialTabState = initialTabStateRef.current;

  const [targetType, setTargetType] = useState(initialTabState.targetType);
  const [pageItems, setPageItems] = useState([]);
  const [totalTargetUserCount, setTotalTargetUserCount] = useState(0);
  const [isLastPage, setIsLastPage] = useState(true);
  const [page, setPage] = useState(initialTabState.page);
  const [rowsPerPage, setRowsPerPage] = useState(initialTabState.rowsPerPage);
  const [orderBy, setOrderBy] = useState(initialTabState.orderBy);
  const [order, setOrder] = useState(initialTabState.order);
  const [loading, setLoading] = useState(false);
  const [msgToDisplay, setMsgToDisplay] = useState(customConfig.noDataMsg);
  const [refreshKey, setRefreshKey] = useState(0);
  const shouldForceRefreshRef = useRef(Boolean(location.state?.refresh));
  const [deleteDialog, setDeleteDialog] = useState({ open: false, data: null });
  const [apiFeedbackMsg, setApiFeedbackMsg] = useState({
    open: false,
    type: "info",
    msg: "",
  });

  const canCreate = getInAppNotificationPermission(
    userRoles,
    targetType === "broadcast" ? "createBroadcast" : "createUserSpecific",
    userLevel,
  );
  const canEdit = getInAppNotificationPermission(userRoles, "editNotification", userLevel);
  const canDelete = getInAppNotificationPermission(userRoles, "deleteNotification", userLevel);
  const canViewTargetUserCount = getInAppNotificationPermission(
    userRoles,
    "viewTargetUserCount",
    userLevel,
  );

  const buildListRouteState = useCallback(
    (extra = {}) =>
      buildNotificationListRouteState({
        targetType,
        listState: buildNotificationListState({
          page,
          rowsPerPage,
          orderBy,
          order,
          targetType,
        }),
        ...extra,
      }),
    [page, rowsPerPage, orderBy, order, targetType],
  );

  useEffect(() => {
    const navigationState = location.state || {};

    if (navigationState.notificationTargetType && !navigationState.restoreListPagination) {
      setTargetType(navigationState.notificationTargetType);
    }

    if (navigationState.refresh) {
      invalidateInAppNotificationListCache();
      shouldForceRefreshRef.current = true;
      setPage(0);
      setRefreshKey((prev) => prev + 1);
    }

    if (navigationState.feedback?.msg) {
      setApiFeedbackMsg({
        open: true,
        type: navigationState.feedback.type || "success",
        msg: navigationState.feedback.msg,
      });
      navigate(location.pathname, {
        replace: true,
        state: buildNotificationListRouteState({
          targetType:
            navigationState.notificationTargetType ||
            navigationState.listState?.targetType,
        }),
      });
    }
  }, [location.pathname, location.state, navigate]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setMsgToDisplay(customConfig.FETCHING_DATA);

    const forceRefresh = shouldForceRefreshRef.current;
    const shouldLoadTotal = targetType === "user-specific" && canViewTargetUserCount;
    const totalPromise = shouldLoadTotal
      ? fetchCachedUserSpecificNotifications({ forceRefresh })
          .then((items) => sumTargetUserCounts(items))
          .catch(() => 0)
      : Promise.resolve(0);

    try {
      let result = await fetchInAppNotificationPage({
        page,
        size: rowsPerPage,
        targetType,
        includeExpired: true,
        forceRefresh,
      });

      if (!result.items.length) {
        const response = await getInAppNotifications({
          page,
          size: rowsPerPage,
          includeExpired: true,
          targetType,
        });
        const parsed = parseInAppNotificationList(response?.data, rowsPerPage);
        const fallbackItems = parsed.items.filter((item) =>
          isValidInAppNotificationListItem(item),
        );

        if (fallbackItems.length) {
          result = {
            items: fallbackItems,
            isLastPage: parsed.isLastPage,
            totalElements: parsed.totalElements ?? fallbackItems.length,
          };
        }
      }

      shouldForceRefreshRef.current = false;

      const items = result.items.filter((item) =>
        matchesTargetType(item.targetType, targetType),
      );

      if (page > 0 && items.length === 0) {
        setIsLastPage(true);
        setPage((current) => Math.max(0, current - 1));
        return;
      }

      const resolvedIsLastPage =
        Boolean(result.isLastPage) || items.length < rowsPerPage;

      setPageItems(items);
      setIsLastPage(resolvedIsLastPage);
      setTotalTargetUserCount(await totalPromise);
      setMsgToDisplay(items.length ? customConfig.FETCH_SUCCESS : customConfig.noDataMsg);
    } catch (error) {
      const errorMsg = getInAppNotificationErrorMessage(error, customConfig.FETCH_FAILURE);
      setMsgToDisplay(errorMsg);
      setPageItems([]);
      setIsLastPage(true);
      setTotalTargetUserCount(0);
      setApiFeedbackMsg({ open: true, type: "error", msg: errorMsg });
    } finally {
      setLoading(false);
    }
  }, [canViewTargetUserCount, page, refreshKey, rowsPerPage, targetType]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const paginatedItems = useMemo(
    () => sortNotifications(pageItems, orderBy, order),
    [pageItems, orderBy, order],
  );

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const navigateToView = useCallback(
    (row) => {
      const listState = buildNotificationListState({
        page,
        rowsPerPage,
        orderBy,
        order,
        targetType,
      });

      setPendingListPagination(listState);
      navigate(IN_APP_NOTIFICATION_ROUTES.view(row.id), {
        state: buildNotificationListRouteState({
          targetType,
          listState,
          targetUserCount: row.targetUserCount,
        }),
      });
    },
    [navigate, order, orderBy, page, rowsPerPage, targetType],
  );

  const columns = useMemo(
    () =>
      createInAppNotificationColumns(
        {
          onEdit: (row) =>
            navigate(IN_APP_NOTIFICATION_ROUTES.edit(row.id), {
              state: buildListRouteState({ targetUserCount: row.targetUserCount }),
            }),
          onView: navigateToView,
          onDelete: (row) => setDeleteDialog({ open: true, data: row }),
          canEdit,
          canDelete,
        },
        { compact: isCompactTable },
      ),
    [buildListRouteState, canDelete, canEdit, isCompactTable, navigate, navigateToView],
  );

  const tableMessage = msgToDisplay;

  if (loading) {
    return <ScreenLoader message={msgToDisplay} />;
  }

  return (
    <>
      <Box sx={{ py: 1, width: "100%", minWidth: 0, boxSizing: "border-box" }}>
        <Box
          sx={{
            mb: 1.5,
            pb: 1.5,
            borderBottom: `1px solid ${colors?.ghost?.light}`,
          }}
        >
          <Box sx={inAppToolbarRowSx}>
            <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                Target Type
              </Typography>
              <AppRadioGroup
                row
                name="notification-target-type"
                value={targetType}
                onChange={(e) => {
                  const nextTargetType = e.target.value;
                  setTargetType(nextTargetType);
                  setPage(0);

                  if (location.state?.notificationTargetType) {
                    navigate(location.pathname, {
                      replace: true,
                      state: buildNotificationListRouteState({
                        listState: buildNotificationListState({
                          page: 0,
                          rowsPerPage,
                          orderBy,
                          order,
                          targetType: nextTargetType,
                        }),
                      }),
                    });
                  }
                }}
                options={TARGET_TYPE_OPTIONS}
                sx={inAppRadioGroupResponsiveSx}
              />
            </Box>
            {canCreate && (
              <Button
                variant="primary"
                size="medium"
                startIcon={<AddIcon />}
                onClick={() =>
                  navigate(
                    `${IN_APP_NOTIFICATION_ROUTES.create}?targetType=${encodeURIComponent(targetType)}`,
                    { state: buildListRouteState() },
                  )
                }
                sx={inAppPrimaryActionSx}
              >
                Create Notification
              </Button>
            )}
          </Box>

          {targetType === "user-specific" && canViewTargetUserCount && (
            <NotificationTargetUserSummary
              targetUserCount={totalTargetUserCount}
              label={TOTAL_TARGETED_USERS_LABEL}
            />
          )}
        </Box>

        <Box sx={inAppListTableShellSx}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              height: "100%",
              backgroundColor: "#F5F5F5",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
              zIndex: 0,
              borderRadius: 1,
            }}
          />
          <Box
            sx={{
              flex: 1,
              position: "relative",
              zIndex: 1,
              minHeight: 0,
              height: "100%",
              overflow: "hidden",
            }}
          >
            <DataTable
              columns={columns}
              data={paginatedItems}
              orderBy={orderBy}
              order={order}
              onSort={handleRequestSort}
              rowKey="id"
              striped
              hoverEffect
              maxHeight="100%"
              sx={{ height: "100%" }}
              tableContainerSx={{ height: "100%", maxHeight: "100%" }}
              msgForDisplay={tableMessage}
            />
          </Box>

          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              backgroundColor: "white",
              borderTop: "1px solid #F5F5F5",
              flexShrink: 0,
            }}
          >
            <IncrementalPagination
              page={page}
              pageSize={rowsPerPage}
              dataLength={paginatedItems.length}
              isLastPage={isLastPage}
              pageSizeOptions={[10, 20, 50]}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(size) => {
                setPage(0);
                setRowsPerPage(size);
              }}
            />
          </Box>
        </Box>
      </Box>

      {deleteDialog.open && (
        <DeleteNotificationDialog
          open={deleteDialog.open}
          notification={deleteDialog.data}
          onClose={(result) => {
            setDeleteDialog({ open: false, data: null });
            if (result?.success) {
              invalidateInAppNotificationListCache();
              shouldForceRefreshRef.current = true;
              setPage(0);
              setRefreshKey((prev) => prev + 1);
            }
          }}
          feedbackHandler={(feedback) =>
            setApiFeedbackMsg({ ...feedback, open: true })
          }
        />
      )}

      <AppSnackbar
        open={apiFeedbackMsg.open}
        type={apiFeedbackMsg.type}
        message={apiFeedbackMsg.msg}
        onClose={() => setApiFeedbackMsg((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default NotificationsTab;
